/**
 * Apps parceiros — helpers de mensagem e IDs especiais.
 */

export const WEB_PLAYER_OPTION_ID = '__web_player__';
export const AURAX_PARTNER_APP_TAG = 'aurax_partner_app:';

/** Creation always requires a deliberate choice, including the own app and Web Player. */
export function defaultCreationAppId() { return ''; }

/** Pricing classification comes from the catalog, never from the app name. */
export function partnerAppLicenseInfo(app) {
  if (!app) return { kind: 'empty', label: 'Selecione um aplicativo', description: 'A escolha do aplicativo é obrigatória para continuar.' };
  if (String(app.id) === WEB_PLAYER_OPTION_ID) return { kind: 'included', label: 'Sem licença de aplicativo', description: 'Acesso pelo navegador ou lista M3U. O valor do plano permanece separado.' };
  if (app.license_kind === 'paid') {
    const days = Number.isInteger(app.trial_days) && app.trial_days > 0 && app.trial_days <= 60 ? app.trial_days : null;
    const trial = days ? `Teste inicial de ${days} ${days === 1 ? 'dia' : 'dias'}, sujeito à elegibilidade do dispositivo.` : 'Consulte no aplicativo a disponibilidade e a duração do teste inicial.';
    const note = String(app.trial_note || '').trim();
    const normalize = text => text.replace(/\s+/g, ' ').replace(/[.。]$/, '').toLocaleLowerCase('pt-BR').trim();
    return { kind: 'paid', label: 'Licença paga', description: 'A licença do aplicativo é cobrada separadamente do plano. Criar um teste ou cliente não paga nem ativa essa licença.',
      trial, note: normalize(note) === normalize(trial) ? '' : note };
  }
  if (app.license_kind === 'free') return { kind: 'free', label: 'Aplicativo gratuito', description: 'Sem cobrança de licença do aplicativo, conforme o catálogo. O valor do plano permanece separado.' };
  if (app.catalog_kind === 'own') return { kind: 'included', label: 'Aplicativo próprio incluído', description: 'Use o aplicativo publicado desta conta. O valor do plano permanece separado.' };
  return { kind: 'unclassified', label: 'Licença não informada', description: 'Confirme a condição da licença antes de orientar o cliente.' };
}

/** Access codes classify the shared catalog; Downloader codes only install APKs. */
export function partnerAppCatalogKind(app) {
  if (app?.catalog_kind === 'own') return 'own';
  return String(app?.access_code || '').trim() ? 'partner' : 'universal';
}

export function partnerAppIdFromNotes(notes) {
  const s = String(notes || '');
  const idx = s.indexOf(AURAX_PARTNER_APP_TAG);
  if (idx < 0) return '';
  let rest = s.slice(idx + AURAX_PARTNER_APP_TAG.length);
  for (const sep of ['|', ' ', '\n', '\r', '\t']) {
    const p = rest.indexOf(sep);
    if (p >= 0) rest = rest.slice(0, p);
  }
  return rest.trim();
}

/** Resolve app a partir das notas da linha + catálogo carregado. */
export function resolvePartnerApp(clientOrNotes, apps = [], webPlayerOption = null) {
  const notes =
    typeof clientOrNotes === 'string'
      ? clientOrNotes
      : clientOrNotes?.admin_notes || clientOrNotes?.admin_notes_display || '';
  const aid = partnerAppIdFromNotes(notes);
  if (!aid) return null;
  if (aid === WEB_PLAYER_OPTION_ID) {
    return (
      webPlayerOption || {
        id: WEB_PLAYER_OPTION_ID,
        name: 'Só Web Player / M3U',
        devices_label: 'Web / qualquer dispositivo',
      }
    );
  }
  return (apps || []).find((a) => String(a.id) === String(aid)) || {
    id: aid, name: aid.startsWith('own_player:') ? 'Aplicativo próprio indisponível' : aid,
  };
}

export function partnerAppLogoSrc(url, apiBase = '') {
  const u = String(url || '').trim();
  if (!u) return null;
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  const base = String(apiBase || '').replace(/\/+$/, '');
  return `${base}${u.startsWith('/') ? '' : '/'}${u}`;
}

export function buildAppSnippet(app, { detail = false } = {}) {
  if (!app) return '';
  if (String(app.id) === WEB_PLAYER_OPTION_ID) {
    return detail
      ? '▶️ *WEB PLAYER*\n\nAssista pelo navegador, *sem instalar aplicativo*.\nUse o link do Web Player enviado com os dados de acesso.'
      : '▶️ *WEB PLAYER*\n\nAssista pelo navegador, sem instalar outro aplicativo.\nLink: {webplayer}';
  }
  const custom = String(
    (detail ? app.message_detail || app.detail_preview : null) ||
      app.message_snippet ||
      app.snippet_preview ||
      '',
  ).trim();
  if (custom) return custom;

  const name = String(app.name || 'Aplicativo').trim();
  const lines = [`📲 *${name.toUpperCase()}*`];
  const where = String(app.where_to_find || '').trim();
  if (where) {
    lines.push('', '📍 Onde encontrar:', where);
  }
  const code = String(app.access_code || '').trim();
  if (code) {
    lines.push('', `🔑 Código de acesso: *${code}*`);
  }
  const dl = String(app.downloader_code || '').trim();
  if (dl) lines.push(`⬇️ Código no Downloader: *${dl}*`);
  const url = String(app.download_url || '').trim();
  const downloaderUrl = String(app.downloader_url || '').trim();
  if (downloaderUrl && downloaderUrl !== url) lines.push(`🔗 URL Downloader: ${downloaderUrl}`);
  if (url) lines.push(`🔗 Download: ${url}`);
  if (detail) {
    const devices = Array.isArray(app.devices)
      ? app.devices.join(', ')
      : String(app.devices_label || '').trim();
    if (devices) lines.push('', `✅ Recomendado para: ${devices}`);
  }
  return lines.join('\n').trim();
}

export function fillAppPlaceholders(block, vars = {}) {
  let out = String(block || '');
  const web = vars.webplayer || vars.webplayer_url || '';
  out = out.split('{webplayer}').join(web);
  out = out.split('{username}').join(vars.username || '');
  out = out.split('{password}').join(vars.password || '');
  out = out.split('{dns}').join(vars.dns || vars.dns_label || '');
  return out;
}
